import { Controller } from '@hotwired/stimulus'

// 培训项目搜索选择器：按「主管部门 → 培训种类 → 项目」三级展示
// 默认收起培训种类，点击后再展开具体项目；搜索时自动展开匹配项
export default class extends Controller {
  static targets = ['input', 'label', 'modal', 'search', 'list']
  static values = { items: Array }

  connect() {
    this.expandedKeys = new Set()
    this.renderList()
    this.restoreSelection()
  }

  disconnect() {
    document.body.classList.remove('overflow-hidden')
  }

  open(event) {
    event.preventDefault()
    this.modalTarget.classList.remove('hidden')
    document.body.classList.add('overflow-hidden')

    this.searchTarget.value = ''
    this.expandSelectedCategory()
    this.renderList()

    requestAnimationFrame(() => {
      this.searchTarget.focus()
      this.scrollToSelected()
    })
  }

  close(event) {
    if (event) event.preventDefault()

    this.modalTarget.classList.add('hidden')
    document.body.classList.remove('overflow-hidden')
  }

  filter() {
    this.renderList()
  }

  // 搜索框里按回车不应提交绑定表单
  preventSubmit(event) {
    event.preventDefault()
  }

  toggleCategory(event) {
    event.preventDefault()

    const key = event.currentTarget.dataset.key
    if (this.expandedKeys.has(key)) {
      this.expandedKeys.delete(key)
    } else {
      this.expandedKeys.add(key)
    }

    this.renderList()
  }

  select(event) {
    event.preventDefault()

    const id = event.currentTarget.dataset.id
    const item = this.itemsValue.find(model => String(model.id) === String(id))
    if (!item) return

    this.inputTarget.value = item.id
    this.setLabel(item.name)
    this.close()
  }

  restoreSelection() {
    const item = this.selectedItem()
    if (item) this.setLabel(item.name)
  }

  selectedItem() {
    const selectedId = String(this.inputTarget.value || '')
    if (!selectedId) return null

    return this.itemsValue.find(model => String(model.id) === selectedId)
  }

  expandSelectedCategory() {
    const item = this.selectedItem()
    if (item) this.expandedKeys.add(this.groupKey(item))
  }

  scrollToSelected() {
    const selected = this.listTarget.querySelector('[data-selected="true"]')
    if (selected) selected.scrollIntoView({ block: 'center' })
  }

  setLabel(name) {
    this.labelTarget.textContent = name
    this.labelTarget.classList.remove('text-gray-400')
    this.labelTarget.classList.add('text-gray-900')
  }

  groupKey(item) {
    return `${item.root}\u0000${item.category}`
  }

  matches(text, keyword) {
    if (!keyword) return true
    return (text || '').toLowerCase().includes(keyword)
  }

  renderList() {
    const keyword = (this.searchTarget?.value || '').trim().toLowerCase()
    const searching = keyword.length > 0
    const selectedId = String(this.inputTarget.value || '')

    const groups = new Map()
    this.itemsValue.forEach(item => {
      const key = this.groupKey(item)
      if (!groups.has(key)) groups.set(key, { key, root: item.root, category: item.category, items: [] })
      groups.get(key).items.push(item)
    })

    const visibleGroups = []
    groups.forEach(group => {
      // 匹配到主管部门或培训种类时，展示该分类下的全部项目
      const categoryMatched = this.matches(group.root, keyword) || this.matches(group.category, keyword)
      const items = categoryMatched ? group.items : group.items.filter(item => this.matches(item.name, keyword))
      if (items.length > 0) visibleGroups.push({ ...group, items })
    })

    this.listTarget.innerHTML = ''

    if (visibleGroups.length === 0) {
      const empty = document.createElement('div')
      empty.className = 'py-10 text-center text-sm text-gray-400'
      empty.textContent = '没有找到相关培训项目'
      this.listTarget.appendChild(empty)
      return
    }

    let currentRoot = null

    visibleGroups.forEach(group => {
      if (group.root !== currentRoot) {
        currentRoot = group.root

        const rootElement = document.createElement('div')
        rootElement.className = 'bg-gray-50 px-4 py-2 text-xs font-medium tracking-wide text-gray-500'
        rootElement.textContent = currentRoot
        this.listTarget.appendChild(rootElement)
      }

      const expanded = searching || this.expandedKeys.has(group.key)

      const header = document.createElement('button')
      header.type = 'button'
      header.dataset.key = group.key
      header.className = 'flex w-full items-center gap-2 border-b border-gray-50 px-4 py-2.5 text-left text-sm font-semibold text-gray-800 hover:bg-gray-50'
      header.addEventListener('click', this.toggleCategory.bind(this))

      const chevron = document.createElement('span')
      chevron.className = `text-gray-400 transition-transform ${expanded ? 'rotate-90' : ''}`
      chevron.textContent = '›'
      header.appendChild(chevron)

      const name = document.createElement('span')
      name.textContent = group.category
      header.appendChild(name)

      const count = document.createElement('span')
      count.className = 'ml-auto text-xs font-normal text-gray-400'
      count.textContent = group.items.length
      header.appendChild(count)

      this.listTarget.appendChild(header)

      if (!expanded) return

      const itemWrapper = document.createElement('div')
      itemWrapper.className = 'ml-7 border-l border-gray-200'

      group.items.forEach(item => {
        const selected = String(item.id) === selectedId

        const button = document.createElement('button')
        button.type = 'button'
        button.dataset.id = item.id
        button.dataset.selected = selected ? 'true' : 'false'
        button.className = `w-full py-2.5 pl-4 pr-4 text-left text-sm active:bg-gray-100 ${
          selected ? 'bg-blue-50 font-medium text-blue-600' : 'text-gray-600 hover:bg-gray-50'
        }`
        button.textContent = item.name
        button.addEventListener('click', this.select.bind(this))
        itemWrapper.appendChild(button)
      })

      this.listTarget.appendChild(itemWrapper)
    })
  }
}
